// kanbanTransitionPolicy — single source of truth for "may this task move to
// that workflow state?" on the Kanban board (cards, list rows, drag-and-drop).
//
// ROLE
// Replaces the old kanbanWorkflowStore: a module-level (no-React) policy that a
// KanbanCard plugin / KanbanListRow / KanbanBoard can query without threading the
// taskTypes collection, the user's project roles, and every task through the DnD
// prop chain. It is a PRE-FILTER for UX only — the backend (TransitionValidator +
// group permissions + the T28 subtask-closure guard) remains the final authority.
// We never widen what the server allows; we only hide moves the server would
// reject, and surface a French reason when one is blocked.
//
// CONTRACT CONSUMED (produced by the back, see CDC-TMA recette 3)
//   taskTypes : collection of task_type terms. Each item resolves to
//     { data: { id, name, workflow: { workflowStates, workflowTransitions } } }
//     (the bare item without `.data` is also tolerated, like the legacy store).
//       workflowStates[stateId] = { label, weight, published,
//           isFinal, isClosed, requiresClosedSubtasks }
//       workflowTransitions[tid] = { label, from:[stateId], to, weight, allowedRoles }
//   userRoles : string[] — ALL of the current user's group_role machine ids in
//     this project (cumulative), i.e. currentUserRoleData.roles from
//     GET /api/project/{group}/role.
//   allTasks  : the loaded tasks, each { data: { id, parentId, type, state } } —
//     needed to resolve a parent's open children for the subtask guard.
//
// A task carries its workflow state in `task.data.state` and its task_type id in
// `task.data.type`. A child task references its parent via `task.data.parentId`.

// ── Module-level context (set once per board load by KanbanBoard) ─────────────
let _types = [];
let _userRoles = [];
let _tasks = [];

// Ce contexte vit HORS de React, et il est semé de façon ASYNCHRONE (les rôles
// projet arrivent d'un fetch). Un composant qui l'interroge au rendu — la
// pastille d'état — calculait donc ses cibles avant le semis et n'avait aucune
// raison de recalculer ensuite : le contrôle restait inerte jusqu'à ce qu'un
// autre changement provoque un rendu. D'où cet abonnement minimal, à consommer
// avec useSyncExternalStore.
let _version = 0;
const _subscribers = new Set();

/** S'abonne aux changements de contexte ; rend la fonction de désabonnement. */
export function subscribePolicy(onChange) {
  _subscribers.add(onChange);
  return () => _subscribers.delete(onChange);
}

/** Instantané du contexte : change à chaque semis. */
export function getPolicyVersion() {
  return _version;
}

/**
 * Seed the policy context. Called by KanbanBoard (replacing setWorkflowTypes).
 * @param {Object} ctx
 * @param {Array}  ctx.taskTypes  task_type collection (items with .data.workflow)
 * @param {string[]} ctx.userRoles  current user's group_role machine ids (cumulative)
 * @param {Array}  ctx.allTasks   loaded tasks (for the subtask guard)
 */
export function setPolicyContext({ taskTypes, userRoles, allTasks } = {}) {
  _types = Array.isArray(taskTypes) ? taskTypes : [];
  _userRoles = Array.isArray(userRoles) ? userRoles.map(String) : [];
  _tasks = Array.isArray(allTasks) ? allTasks : [];
  _version += 1;
  _subscribers.forEach((fn) => fn());
}

// ── Internal resolvers ────────────────────────────────────────────────────────

// Unwrap a collection item to its data payload, tolerating both the syncable
// shape ({ data: {...} }) and a bare object — same defensiveness as the old store.
const dataOf = (x) => (x && x.data) ? x.data : x;

/** Resolve the full workflow ({workflowStates, workflowTransitions}) of a type id. */
function workflowOf(typeId) {
  const tt = _types.find((t) => String(dataOf(t)?.id) === String(typeId));
  return dataOf(tt)?.workflow || null;
}

/** Resolve a task's task_type id (handles both task and bare-data shapes). */
const typeIdOf = (task) => dataOf(task)?.type;

/**
 * Whether a state is "closed" (final or a cancellation state) per the workflow
 * of a GIVEN type. Used by the subtask guard, where each child must be resolved
 * against ITS OWN type's workflow — not the parent's.
 */
export function isStateClosed(typeId, stateId) {
  if (!stateId) return false;
  const states = workflowOf(typeId)?.workflowStates || {};
  return states[stateId]?.isClosed === true;
}

/**
 * Children of a task that are NOT closed (per each child's OWN workflow).
 * A child is any loaded task whose data.parentId === task.data.id.
 */
function openChildrenOf(task) {
  const parentId = dataOf(task)?.id;
  if (parentId == null) return [];
  return _tasks.filter((child) => {
    const c = dataOf(child);
    if (c == null) return false;
    if (String(c.parentId) !== String(parentId)) return false;
    return !isStateClosed(c.type, c.state);
  });
}

// ── Public API (signatures fixed by POLICY_SPEC) ──────────────────────────────

/**
 * Resolve a workflow state's human label from the type's OWN workflow — correct
 * for both parent tasks and subtasks even when their workflows differ.
 * (Preserved verbatim from the legacy store.)
 * @param {string|number} typeId  the task's field_task_type term id
 * @param {string} stateId        a workflow state id
 * @returns {string} the state label, or '' if unknown
 */
export function getStateLabel(typeId, stateId) {
  if (!stateId) return '';
  const states = workflowOf(typeId)?.workflowStates || {};
  return states[stateId]?.label || '';
}

/**
 * Target states the task may move to: reachable by the type's workflow,
 * permitted for the user's roles, AND not blocked by the subtask guard.
 * @param {Object} task  { data: { id, type, state, parentId } }
 * @returns {{to: string, label: string}[]}
 */
export function getAllowedTargets(task) {
  const d = dataOf(task);
  const currentState = d?.state;
  if (!currentState) return [];

  const typeId = typeIdOf(task);
  const wf = workflowOf(typeId);
  const transitions = wf?.workflowTransitions;
  const states = wf?.workflowStates || {};
  // No workflow for this type → no UI restriction, but also no transition list
  // to offer (parity with the legacy getTransitionTargets behaviour).
  if (!transitions) return [];

  const seen = new Set();
  const out = [];
  Object.values(transitions).forEach((tr) => {
    if (!Array.isArray(tr.from) || !tr.from.includes(currentState)) return;
    if (tr.to === currentState || seen.has(tr.to)) return;
    // Apply the SAME gate used by canTransition so the offered list and the
    // drop/menu validation can never diverge.
    if (!canTransition(task, tr.to).ok) return;
    seen.add(tr.to);
    out.push({ to: tr.to, label: states[tr.to]?.label || tr.label || tr.to });
  });
  return out;
}

/**
 * Can `task` transition to `toState`? Pre-filter for UX; the server re-checks.
 * Reason priority: 'workflow' → 'permission' → 'subtasks' → null (ok).
 * @param {Object} task  { data: { id, type, state, parentId } }
 * @param {string} toState  candidate target workflow state id
 * @returns {{ ok: boolean, reason: ('workflow'|'permission'|'subtasks'|null), message: string }}
 */
export function canTransition(task, toState) {
  const d = dataOf(task);
  const currentState = d?.state;
  const typeId = typeIdOf(task);
  const wf = workflowOf(typeId);
  const transitions = wf?.workflowTransitions;
  const states = wf?.workflowStates || {};

  // No workflow on this type → no restriction at all (server is the authority).
  if (!transitions) return ok();

  // 1) reachable: a transition exists with from ∋ currentState && to === toState.
  const matching = Object.values(transitions).filter(
    (tr) => Array.isArray(tr.from) && tr.from.includes(currentState) && tr.to === toState,
  );
  if (matching.length === 0) {
    return fail('workflow', 'Déplacement impossible : transition non prévue par le workflow de ce type de tâche.');
  }

  // 2) permitted: at least one matching transition is allowed for the user's roles.
  //    TOLERANCE: if allowedRoles is ABSENT/undefined on a transition (contract
  //    not yet deployed), do NOT gate on permission — so we never mask everything
  //    while the back hasn't started exposing allowedRoles.
  // Until the user's project roles are known (the /api/project/{id}/role fetch is
  // async and may not have seeded _userRoles yet, esp. on the task sheet), do NOT
  // gate on permission — otherwise every transition would be transiently hidden /
  // refused right after load. The server (TransitionValidator) stays the authority.
  const rolesKnown = _userRoles.length > 0;
  const permitted = matching.some((tr) => {
    if (!rolesKnown) return true; // roles not seeded yet → don't pre-block
    if (tr.allowedRoles === undefined || tr.allowedRoles === null) return true; // tolerate missing contract
    if (!Array.isArray(tr.allowedRoles) || tr.allowedRoles.length === 0) return false;
    return tr.allowedRoles.some((r) => _userRoles.includes(String(r)));
  });
  if (!permitted) {
    return fail('permission', 'Déplacement impossible : votre rôle sur ce projet ne permet pas cette transition.');
  }

  // 3) subtasks: entering a state flagged requiresClosedSubtasks is blocked while
  //    the task still has ≥1 open child (each child resolved via its OWN workflow).
  if (states[toState]?.requiresClosedSubtasks === true) {
    const open = openChildrenOf(task);
    if (open.length > 0) {
      const n = open.length;
      return fail(
        'subtasks',
        `Déplacement impossible : ${n} sous-tâche(s) encore ouverte(s) doivent être terminées d'abord.`,
      );
    }
  }

  return ok();
}

// ── Result helpers ────────────────────────────────────────────────────────────
const ok = () => ({ ok: true, reason: null, message: '' });
const fail = (reason, message) => ({ ok: false, reason, message });
